import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { getTurnstileMode, getTurnstileSiteKey, isTurnstileEnabled } from '@/lib/turnstile-utils';
import type { AppConfig } from '@/types/config';

// An origin cannot change without a document navigation. The server snapshot
// also runs during initial hydration, before React switches to the browser one.
const subscribeToOrigin = () => () => undefined;
const getBrowserOrigin = () => window.location.origin;
const getServerOrigin = () => null;

export const useTurnstile = (action: string) => {
    const config = useConfig<AppConfig>();
    const origin = useSyncExternalStore(subscribeToOrigin, getBrowserOrigin, getServerOrigin);
    const [token, setToken] = useState<string | null>(null);
    const resetRef = useRef<(() => void) | null>(null);
    /** Set by the widget in non-interactive/invisible mode; starts the challenge. */
    const executeRef = useRef<(() => void) | null>(null);
    const enabled = config ? isTurnstileEnabled(config) : false;
    const mode = config ? getTurnstileMode(config) : 'managed';
    const siteKey = useMemo(() => {
        if (!config || !enabled || !origin) {
            return null;
        }

        return getTurnstileSiteKey(config, origin);
    }, [config, enabled, origin]);
    const isPending = enabled && Boolean(siteKey) && !token;

    const handleSuccess = useCallback((nextToken: string) => {
        setToken(nextToken);
    }, []);

    const handleError = useCallback(() => {
        setToken(null);
    }, []);

    const reset = useCallback(() => {
        setToken(null);
        resetRef.current?.();
    }, []);

    return {
        action,
        enabled,
        mode,
        siteKey,
        token,
        isPending,
        resetRef,
        executeRef,
        handleSuccess,
        handleError,
        handleExpire: handleError,
        reset,
    };
};
